"use strict";
/**
 * @name Table
 *
 * @description
 *  A Table component that works like a helper in composing the Panel components and various other options.
 *    See the Teamsnap patterns library for more information.  https://teamsnap-ui-patterns.netlify.com/patterns/components/table.html
 *
 * @example
 *  <Table
 *    defaultSort='col2'
 *    columns=[
 *      { name: 'col1', label: 'Column One', isSortable: true },
 *      { name: 'col2', label: 'Column Two', isSortable: true },
 *      { name: 'col3', label: 'Column Three', render: (column, row) => <a href={`/path/to/${row.id}`}>{ row.col3 }</a> }
 *    ]
 *    rows=[
 *      { id: 1, col1: 'Row 1 Column One', col2: 'Row 1 Column 2', col3: 'Row 1 Column 3' },
 *      { id: 2, col1: 'Row 2 Column One', col2: 'Row 2 Column 2', col3: 'Row 2 Column 3' },
 *      { id: 3, col1: 'Row 3 Column One', col2: 'Row 3 Column 2', col3: 'Row 3 Column 3' }
 *    ] />
 *
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = __importStar(require("react"));
var PropTypes = __importStar(require("prop-types"));
var helpers_1 = require("../../utils/helpers");
var sort_1 = require("../../utils/sort");
var Icon_1 = require("../Icon");
var Panel_1 = require("../Panel");
var PanelBody_1 = require("../PanelBody");
var PanelRow_1 = require("../PanelRow");
var PanelCell_1 = require("../PanelCell");
var TextLink_1 = require("../TextLink");
var Loader_1 = require("../Loader");
var propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        render: PropTypes.func,
        isSortable: PropTypes.bool,
        sortType: PropTypes.string,
        sortFn: PropTypes.func,
        align: PropTypes.oneOf(['right', 'left', 'center']),
        mods: PropTypes.string,
        style: PropTypes.object,
        otherProps: PropTypes.object,
        testId: PropTypes.string,
    })),
    rows: PropTypes.arrayOf(PropTypes.object),
    defaultSort: PropTypes.string,
    externalSortingFunction: PropTypes.func,
    className: PropTypes.string,
    mods: PropTypes.string,
    style: PropTypes.object,
    testId: PropTypes.string,
    otherProps: PropTypes.object,
    maxTableHeight: PropTypes.string,
    isLoading: PropTypes.bool,
    placeHolder: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};
var sortItems = function (sortColumns, newItems, sortByColumn, sortByReverse) {
    var sortColumn = sortColumns.find(function (c) { return c.name === sortByColumn; });
    if (!sortColumn) {
        return newItems;
    }
    var name = sortColumn.name, sortType = sortColumn.sortType, sortFn = sortColumn.sortFn;
    return (0, sort_1.sortBy)(newItems, {
        name: name,
        sortType: sortType,
        sortFn: sortFn,
        isReverse: sortByReverse,
    });
};
var Table = function (_a) {
    var className = _a.className, mods = _a.mods, style = _a.style, testId = _a.testId, otherProps = _a.otherProps, maxTableHeight = _a.maxTableHeight, placeHolder = _a.placeHolder, isLoading = _a.isLoading, columns = _a.columns, externalSortingFunction = _a.externalSortingFunction, defaultSort = _a.defaultSort, rows = _a.rows;
    var _b = React.useState([]), items = _b[0], setItems = _b[1];
    var _c = React.useState(null), sortBy = _c[0], setSortBy = _c[1];
    var _d = React.useState('down'), sortDirection = _d[0], setSortDirection = _d[1];
    // We should remove this effect and create a prop called defaultSortDirection
    React.useEffect(function () {
        var sortDir = defaultSort.charAt(0) === '-';
        var sortName = sortDir ? defaultSort.substr(1) : defaultSort;
        setSortDirection(sortDir ? 'up' : 'down');
        setSortBy(sortName);
    }, [defaultSort]);
    React.useEffect(function () {
        setItems(rows);
    }, [rows, setItems]);
    var handleSortClick = function (e) {
        e.preventDefault();
        var sortName = e.currentTarget.getAttribute('href');
        var isCurrentSortedColumn = sortName === sortBy;
        var sortDir = isCurrentSortedColumn && sortDirection === 'down';
        // If an function is provided here, we let the parent component figure out the sorting
        // This is valuable when we sort beyond the data thats currently in the table
        // IE: We keep data on the server and want to sort against that or are supporting pagination.
        setSortDirection(sortDir ? 'up' : 'down');
        if (externalSortingFunction != null) {
            setSortBy(sortName);
            externalSortingFunction(sortName, sortDir);
        }
        else {
            var tableState = sortItems(columns, items, sortName, sortDir);
            setItems(tableState);
            setSortBy(sortName);
        }
    };
    var renderPanelCell = function (role, children, column) {
        var cellMods = (0, helpers_1.getClassName)("u-text".concat((0, helpers_1.capitalize)(column.align || 'Left')), column.mods, role === 'columnheader' && column.isSortable ? 'Panel-cell--sortable' : '');
        return (React.createElement(PanelCell_1.PanelCell, __assign({ key: column.key, mods: cellMods, role: role, style: column.style, isTitle: column.isTitle }, column.otherProps, { testId: "".concat(column.testId, " row") }), children));
    };
    var renderColumn = function (column, row) {
        var data = row[column.name];
        var children = column.render ? column.render(column, row) : data;
        return renderPanelCell('cell', children, __assign({ key: "".concat(row.id, "-").concat(column.name), itTitle: false }, column));
    };
    var columnsJsx = columns.map(function (column) {
        var activeColumn = items.length && column.name === sortBy;
        var textLinkMods = (0, helpers_1.getClassName)('u-flex', 'u-flexAlignItemsCenter', column.align === 'right' && 'u-flexJustifyEnd u-spaceNegativeRightSm', column.align === 'center' && 'u-flexJustifyCenter');
        var children = column.isSortable ? (React.createElement(TextLink_1.TextLink, { location: column.name, onClick: handleSortClick, mods: textLinkMods, otherProps: { 'data-testid': "".concat(column.testId, " columnheader") } },
            React.createElement("span", { className: "u-colorInfo u-textNoWrap u-flex u-flexAlignItemsCenter" }, column.label),
            React.createElement("div", { className: "u-colorNeutral5 u-fontSizeXs u-spaceLeftXs" },
                React.createElement(Icon_1.Icon, { name: activeColumn ? sortDirection : 'down', mods: activeColumn && 'u-colorPrimary' })))) : (React.createElement("span", { "data-testid": "".concat(column.testId, " columnheader"), className: "u-colorInfo u-textNoWrap u-flex u-flexAlignItemsCenter ".concat(textLinkMods) }, column.label));
        return renderPanelCell('columnheader', children, __assign({ key: column.name, isTitle: true }, column));
    });
    var withMaxTableHeight = { height: maxTableHeight, overflow: 'scroll' };
    return (React.createElement(Panel_1.Panel, __assign({ className: className, mods: mods, style: style, testId: testId }, otherProps),
        React.createElement(PanelBody_1.PanelBody, { role: "table" },
            React.createElement(PanelRow_1.PanelRow, { isWithCells: true }, columnsJsx),
            isLoading ? (React.createElement("div", { className: "u-padMd u-textCenter" },
                React.createElement(Loader_1.Loader, { type: "spin", text: "loading..." }))) : (React.createElement(React.Fragment, null, !items.length ? (React.createElement("div", { className: "u-padMd u-textCenter" }, placeHolder)) : (React.createElement("div", { style: maxTableHeight ? withMaxTableHeight : null }, items.map(function (row, index) { return (React.createElement(PanelRow_1.PanelRow, { key: row.id, isWithCells: true, className: row.selected && row.onClick ? 'Panel-row Panel-row--selectable' : 'Panel-row', otherProps: { onClick: row.onClick ? row.onClick : null }, testId: "table-panel-row-".concat(index) }, columns.map(function (column) { return renderColumn(column, row); }))); }))))))));
};
Table.defaultProps = {
    columns: [],
    rows: [],
    defaultSort: '',
    className: 'Panel',
    mods: null,
    style: {},
    testId: null,
    otherProps: {},
    placeHolder: 'Nothing to see here',
    maxTableHeight: null,
    isLoading: false,
};
exports.default = Table;
